import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@remix-run/react';
import type { PostTheme, WebTheme } from '@shared/dream-components';
import { PaywallElement, SectionElement } from '@shared/dream-components';

import Text from '~/components/Text';
import type { Paywall } from '~/interfaces/paywall';
import type { Subscription } from '~/interfaces/subscriptions';
import { buildLoginPath } from '~/utils/buildPostAuthRedirectUrl';

interface DefaultPremiumGateProps {
  paywall: Paywall;
  subscription?: Subscription;
  primaryTheme?: WebTheme;
}

const DefaultPremiumGate = ({ paywall, subscription, primaryTheme }: DefaultPremiumGateProps) => {
  const { t } = useTranslation();

  const url = useMemo(() => {
    try {
      const baseUrl = new URL(`${window.location.origin}/upgrade${window.location.search}`);

      if (paywall.offer_id) {
        baseUrl.searchParams.set('offer_id', paywall.offer_id);
      }
      return baseUrl.toString();
    } catch {
      return `${window.location.origin}/upgrade${window.location.search}`;
    }
  }, [paywall.offer_id]);

  const loginPath = useMemo(() => buildLoginPath(), []);

  const theme = useMemo((): PostTheme | undefined => {
    if (!primaryTheme) return undefined;

    return {
      paragraph_font_family: primaryTheme.body_font,
      paragraph_font_size: '16',
      paragraph_line_height_size: 'Normal',
      h2_font_family: primaryTheme.header_font,
      h2_font_size: '30',
      h2_line_height_size: 'Normal',
      h4_font_family: primaryTheme.header_font,
      h4_font_size: '20',
      h4_line_height_size: 'Normal',
      button_font_family: primaryTheme.button_font,
      button_border_radius: primaryTheme.border_radius,
      button_padding_top: '12',
      button_padding_bottom: '24',
      quote_3_mark_color: '',
      quote_3_mark_alignment: '',
      quote_3_mark_spacing: '',
      quote_3_mark_font_size: '',
      quote_3_mark_font_family: '',
    };
  }, [primaryTheme]);

  return (
    <SectionElement
      element={{
        type: 'section',
        attrs: {
          id: 'premium-page-gate',
          backgroundColor: primaryTheme?.background_color,
          isViewportHeight: true,
          horizontalAlign: 'center',
          innerVerticalAlign: 'align_center',
          padding: '24px',
        },
        content: [],
      }}
    >
      <div className="mx-auto max-w-xl space-y-4">
        <PaywallElement
          theme={theme}
          upgradeUrl={url}
          upgradePath="/upgrade"
          shouldUseUrl
          isLive
          element={{
            type: 'paywall_break',
            attrs: {
              id: paywall.id,
              data: {
                title: paywall.title,
                description: paywall.description,
                cta_text: paywall.cta_text,
                benefits: paywall.benefits,
                subheader: paywall.subheader,
                image_url: paywall.image_url,
                style: paywall.style,
              },
            },
            content: [{ id: 'text', text: '' }],
          }}
        >
          {null}
        </PaywallElement>

        {!subscription && (
          <Text
            as="span"
            type="body"
            theme="content"
            size="xs"
            className="flex items-center justify-center text-center text-xs tracking-wide opacity-75 sm:text-lg"
          >
            {t('login.already_a_subscriber')}
            <Link to={loginPath} className="ml-1">
              <span className="text-wt-primary underline underline-offset-1">{t('shared.cta.sign_in')}</span>.
            </Link>
          </Text>
        )}
      </div>
    </SectionElement>
  );
};

export default DefaultPremiumGate;
